// Reine Darstellungslogik für die kompakte Einteilungsmatrix.
// CommonJS hält die Helper zusätzlich ohne Browser-/React-Umgebung testbar.

const MIN_COLUMN_WIDTH = 48;
const CHARACTER_WIDTH = 6;
const SEGMENT_HORIZONTAL_PADDING = 14;
const SEGMENT_MIN_WIDTH = 18;
const SEGMENT_GAP = 2;
const CELL_HORIZONTAL_PADDING = 4;

const MAIN_CODE_MAP = {
  ATF: 'AF',
  ATM: 'AM',
  WTF: 'WF',
  WTM: 'WM',
};

const extractVehicleNumber = (funkrufname) => {
  if (!funkrufname) return '';
  return funkrufname.match(/[\/-](\d{1,2})$/)?.[1] || '';
};

const compactMainFunctionCode = (code) => {
  if (!code) return '';
  const match = String(code).match(/^(ATF|ATM|WTF|WTM)(\d*)$/i);
  if (!match) return String(code);
  return `${MAIN_CODE_MAP[match[1].toUpperCase()]}${match[2]}`;
};

const getFullMainFunctionCode = (funktion, fahrzeugTypNummern = {}) => {
  if (!funktion) return '';
  const code = funktion.funktion_kuerzel || '';
  if (code === 'ZUS') {
    const vehicleType = (funktion.fahrzeug_name || '').split(' ')[0] || 'FZ';
    return `+${vehicleType}`;
  }
  if (/\d$/.test(code)) return code;
  const vehicleNumber = fahrzeugTypNummern?.[funktion.fahrzeug_id]
    ?? extractVehicleNumber(funktion.fahrzeug_name || '');
  return vehicleNumber ? `${code}${vehicleNumber}` : code;
};

const compactSpringerVehicleCode = (funktion) => {
  if (!funktion) return '';
  const vehicleName = String(funktion.fahrzeug_name || '').trim();
  const upperName = vehicleName.toUpperCase();
  const isRtwX = upperName.startsWith('RTW')
    && /(?:^|[\s/-])X(?:$|[\s/-])/.test(upperName);
  if (isRtwX) return 'RX';
  if (/^DLK(?:$|[\s/-])/.test(upperName)) return 'DL';
  if (vehicleName) return vehicleName.split(/[\s/]+/)[0];
  return funktion.funktion_kuerzel || '';
};

const getMatrixCellSegments = (einteilung, fahrzeugTypNummern = {}) => {
  if (!einteilung?.hauptfunktion) return [];
  const mainCode = compactMainFunctionCode(
    getFullMainFunctionCode(einteilung.hauptfunktion, fahrzeugTypNummern)
  );
  return [
    { kind: 'haupt', label: mainCode, funktion: einteilung.hauptfunktion },
    ...(einteilung.sprungfunktionen || []).map(funktion => ({
      kind: 'springer',
      label: compactSpringerVehicleCode(funktion),
      funktion,
    })),
  ];
};

const getMatrixCellWidth = (einteilung, fahrzeugTypNummern = {}) => {
  const segments = getMatrixCellSegments(einteilung, fahrzeugTypNummern);
  if (segments.length === 0) return MIN_COLUMN_WIDTH;
  const contentWidth = segments.reduce((sum, segment) => (
    sum + Math.max(
      SEGMENT_MIN_WIDTH,
      segment.label.length * CHARACTER_WIDTH + SEGMENT_HORIZONTAL_PADDING
    )
  ), 0);
  const gapsWidth = Math.max(0, segments.length - 1) * SEGMENT_GAP;
  return Math.max(MIN_COLUMN_WIDTH, contentWidth + gapsWidth + CELL_HORIZONTAL_PADDING);
};

const getMatrixColumnWidths = (
  diensttage = [],
  einteilungen = {},
  fahrzeugTypNummern = {}
) => {
  const employeeAssignments = Object.values(einteilungen || {});
  return Object.fromEntries(diensttage.map(diensttag => {
    const key = `${diensttag.datum}_${diensttag.dienst_art}`;
    const width = employeeAssignments.reduce((largestWidth, assignments) => (
      Math.max(
        largestWidth,
        getMatrixCellWidth(assignments?.[key], fahrzeugTypNummern)
      )
    ), MIN_COLUMN_WIDTH);
    return [key, width];
  }));
};

const syncMatrixHeaderScroll = (matrixBody, stickyHeader) => {
  if (!matrixBody || !stickyHeader) return;
  stickyHeader.scrollLeft = matrixBody.scrollLeft;
};

const getMatrixDragScrollLeft = (scrollLeftStart, pointerStartX, pointerCurrentX) => (
  scrollLeftStart - (pointerCurrentX - pointerStartX)
);

const getGuestRowBackgroundClass = index => (
  index % 2 === 0 ? 'bg-violet-50' : 'bg-violet-100'
);

module.exports = {
  MIN_COLUMN_WIDTH,
  compactMainFunctionCode,
  compactSpringerVehicleCode,
  getMatrixCellSegments,
  getMatrixCellWidth,
  getMatrixColumnWidths,
  getMatrixDragScrollLeft,
  getGuestRowBackgroundClass,
  syncMatrixHeaderScroll,
};
